import { useState } from "react";
import { motion } from "framer-motion";
import { Envelope, Phone, Globe, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";

const SERVICE_OPTIONS = ["Custom Website Design", "Website Redesign", "E-commerce", "Landing Page", "SEO", "Maintenance", "Branding"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "", services: "", description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-black/15 px-4 py-3 outline-none focus:border-black transition-colors";

  return (
    <div>
      <PageHeader overline="Contact" title="Let's start your project." subtitle="Tell us a little about your business and goals. We'll get back to you within one business day." />

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left info */}
          <div className="lg:col-span-4 space-y-10">
            <div>
              <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Reach us</span>
              <div className="mt-5 space-y-4">
                <a href="mailto:kennywebdesign26@gmail.com" className="flex items-center gap-3 hover:text-black text-zinc-700 transition-colors">
                  <Envelope size={22} /> kennywebdesign26@gmail.com
                </a>
                <a href="tel:+18574268855" className="flex items-center gap-3 hover:text-black text-zinc-700 transition-colors">
                  <Phone size={22} /> +1 (857) 426-8855
                </a>
                <div className="flex items-center gap-3 text-zinc-700">
                  <Globe size={22} /> 100% online — serving businesses everywhere
                </div>
              </div>
            </div>

            <div className="border border-black/10 bg-white p-8">
              <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Response time</span>
              <p className="mt-4 text-zinc-700 leading-relaxed">
                We're a fully online studio. Send us a message any time and we'll reply within
                <b> one business day</b>.
              </p>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-8">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-black/10 bg-white p-12 text-center" data-testid="contact-success">
                <CheckCircle size={56} weight="fill" className="mx-auto" />
                <h2 className="mt-6 font-display text-3xl font-black tracking-tighter">Message sent!</h2>
                <p className="mt-3 text-zinc-600">Thanks for reaching out. We've emailed you a confirmation and will reply within one business day.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-black/10 bg-[#FAFAFA] p-6 sm:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium">Name *</label>
                    <input required data-testid="contact-name" className={`mt-2 ${inputCls}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Business name</label>
                    <input data-testid="contact-business" className={`mt-2 ${inputCls}`} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email *</label>
                    <input required type="email" data-testid="contact-email" className={`mt-2 ${inputCls}`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone</label>
                    <input data-testid="contact-phone" className={`mt-2 ${inputCls}`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Services needed</label>
                    <select data-testid="contact-services" className={`mt-2 ${inputCls}`} value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })}>
                      <option value="">Select a service</option>
                      {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-sm font-medium">Project description *</label>
                  <textarea required rows={5} data-testid="contact-description" className={`mt-2 ${inputCls} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell us about your business, goals, and what you're looking for." />
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-black text-white font-medium px-8 py-4 hover:bg-zinc-800 transition-colors disabled:opacity-50">
                  {submitting ? "Sending…" : "Get a Free Consultation"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
